import React, { createContext, useContext } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { formatValue } from './ontologyFormat';
import { dsVar } from '../../theme/tokens';

/**
 * What a control is actually configured to, in a list cell.
 *
 * The value lives on a resolved PARAMETER, not on the concept, so it arrives from a
 * separate per-page request and reaches the cell through this context — `OntologyColumn`
 * hands `render` only the row document, and widening that signature would touch every
 * column in three browsers for the sake of one.
 *
 * The cell is deliberately willing to say it does not know. A control whose threshold
 * varies by jurisdiction has no single value, and printing one anyway would claim a
 * number the engine may not apply — the failure the whole resolution module exists to
 * prevent (see ontologyFormat.ts on why the browser never resolves for itself).
 */

export interface InForceEntry {
    parameter_count: number;
    scoped: boolean;
    resolved: {
        parameter_code?: string;
        value?: unknown;
        unit?: string | null;
        policy_code?: string;
        scope_level?: string;
        source_type?: string;
    } | null;
}

/** `null` while the request is in flight, or after it failed. */
export const InForceContext = createContext<Record<string, InForceEntry> | null>(null);

const Muted: React.FC<{ children: React.ReactNode; title?: string }> = ({ children, title }) => {
    const text = (
        <Typography component="span" sx={{ fontSize: '0.8125rem', color: dsVar('fg-muted') }}>
            {children}
        </Typography>
    );
    return title ? <Tooltip title={title}><span>{text}</span></Tooltip> : text;
};

const InForceCell: React.FC<{ conceptCode?: string }> = ({ conceptCode }) => {
    const map = useContext(InForceContext);

    // Not fetched yet, or the request failed. Empty rather than a spinner or an error —
    // every other column is correct, and a row of spinners on each page turn is the
    // flicker this table was already fixed once to avoid.
    if (!map || !conceptCode) return null;

    const entry = map[conceptCode];
    if (!entry) return null;

    if (entry.resolved) {
        return (
            <Box>
                <Typography
                    component="div"
                    sx={{ fontSize: '0.875rem', color: dsVar('fg-body'), fontWeight: 500 }}
                >
                    {formatValue(entry.resolved.value, entry.resolved.unit)}
                </Typography>
                {entry.resolved.policy_code && (
                    <Typography
                        component="div"
                        sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6875rem', color: dsVar('fg-muted') }}
                    >
                        {entry.resolved.policy_code}
                    </Typography>
                )}
            </Box>
        );
    }

    // Nothing configures this control, so the engine falls back to its own default. This is
    // a coverage gap, shown where the analyst is already looking rather than only in a report.
    if (entry.parameter_count === 0) {
        return (
            <Muted title="No threshold is configured, so the engine applies its built-in default.">
                Not set
            </Muted>
        );
    }

    // A value exists but depends on where the engine is running — jurisdiction, segment,
    // product or template. The detail page can show them all; a single cell cannot.
    if (entry.scoped) {
        return (
            <Muted title="This control is tuned differently depending on jurisdiction, segment or template. Open the record to see each value.">
                Varies by scope
            </Muted>
        );
    }

    return (
        <Muted title="Several thresholds bind this control. Open the record to see each one.">
            {entry.parameter_count} thresholds
        </Muted>
    );
};

export default InForceCell;
